/**
 * useOrbDocGettingStartedIntroductionENDocEx1CountingLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocGettingStartedIntroductionENDocEx1Counting
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocGettingStartedIntroductionENCounter, OrbDocGettingStartedIntroductionENDocEx1CountingEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocGettingStartedIntroductionENDocEx1CountingState =
  | 'ready';

export type OrbDocGettingStartedIntroductionENDocEx1CountingEvent =
  | 'INIT'
  | 'INCREMENT'
  | 'DECREMENT'
  | 'RESET'
  | 'MOVED';

type OrbDocGettingStartedIntroductionENDocEx1CountingSlot = 'main';
interface OrbDocGettingStartedIntroductionENDocEx1CountingInternalState {
  machineState: OrbDocGettingStartedIntroductionENDocEx1CountingState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocGettingStartedIntroductionENDocEx1CountingEvent | null;
  slotWriter: Partial<Record<OrbDocGettingStartedIntroductionENDocEx1CountingSlot, OrbDocGettingStartedIntroductionENDocEx1CountingEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocGettingStartedIntroductionENDocEx1CountingEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocGettingStartedIntroductionENCounter>;
}

type OrbDocGettingStartedIntroductionENDocEx1CountingAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocGettingStartedIntroductionENDocEx1CountingEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocGettingStartedIntroductionENDocEx1CountingSlot[]; renderClear?: OrbDocGettingStartedIntroductionENDocEx1CountingSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocGettingStartedIntroductionENDocEx1CountingEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocGettingStartedIntroductionENCounter & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocGettingStartedIntroductionENDocEx1CountingReducer(
  state: OrbDocGettingStartedIntroductionENDocEx1CountingInternalState,
  action: OrbDocGettingStartedIntroductionENDocEx1CountingAction
): OrbDocGettingStartedIntroductionENDocEx1CountingInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocGettingStartedIntroductionENDocEx1CountingState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocGettingStartedIntroductionENCounter>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocGettingStartedIntroductionENCounter>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocGettingStartedIntroductionENCounter>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocGettingStartedIntroductionENDocEx1CountingInternalState = {
  machineState: 'ready',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocGettingStartedIntroductionENDocEx1CountingConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

export interface OrbDocGettingStartedIntroductionENDocEx1CountingLogicReturn {
  state: OrbDocGettingStartedIntroductionENDocEx1CountingState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocGettingStartedIntroductionENDocEx1CountingEvent | null;
  slotWriter: Partial<Record<OrbDocGettingStartedIntroductionENDocEx1CountingSlot, OrbDocGettingStartedIntroductionENDocEx1CountingEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocGettingStartedIntroductionENDocEx1CountingEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocGettingStartedIntroductionENCounter>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocGettingStartedIntroductionENDocEx1CountingEventPayloadMap]: (payload?: OrbDocGettingStartedIntroductionENDocEx1CountingEventPayloadMap[K]) => Promise<EventResponse> };
  config?: OrbDocGettingStartedIntroductionENDocEx1CountingConfig;
}

export function useOrbDocGettingStartedIntroductionENDocEx1CountingLogic(config?: Partial<OrbDocGettingStartedIntroductionENDocEx1CountingConfig>): OrbDocGettingStartedIntroductionENDocEx1CountingLogicReturn {
  const mergedConfig = config;

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-getting-started-introduction-en-doc-ex1-counting', orbitalName: 'OrbDocGettingStartedIntroductionEN', traitName: 'OrbDocGettingStartedIntroductionENDocEx1Counting' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocGettingStartedIntroductionENDocEx1CountingReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocGettingStartedIntroductionENCounter'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'ready' && event === 'INIT') {
      // ready → ready (on INIT)
      _newState = 'ready';
    } else if (fromState === 'ready' && event === 'INCREMENT') {
      // ready → ready (on INCREMENT)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocGettingStartedIntroductionENDocEx1Counting' }); emitCascadeEvent(eventBus, 'OrbDocGettingStartedIntroductionEN', 'OrbDocGettingStartedIntroductionENDocEx1Counting', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocGettingStartedIntroductionEN', trait: 'OrbDocGettingStartedIntroductionENDocEx1Counting' } }); })({ event: "INCREMENT" as const }));
        _fields['count'] = (+(fields?.count ?? 0) + 1);
        fields = { ...(fields ?? {}), 'count': _fields['count'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'ready';
    } else if (fromState === 'ready' && event === 'DECREMENT') {
      // ready → ready (on DECREMENT)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocGettingStartedIntroductionENDocEx1Counting' }); emitCascadeEvent(eventBus, 'OrbDocGettingStartedIntroductionEN', 'OrbDocGettingStartedIntroductionENDocEx1Counting', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocGettingStartedIntroductionEN', trait: 'OrbDocGettingStartedIntroductionENDocEx1Counting' } }); })({ event: "DECREMENT" as const }));
        _fields['count'] = (+(fields?.count ?? 0) - 1);
        fields = { ...(fields ?? {}), 'count': _fields['count'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'ready';
    } else if (fromState === 'ready' && event === 'RESET') {
      // ready → ready (on RESET)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocGettingStartedIntroductionENDocEx1Counting' }); emitCascadeEvent(eventBus, 'OrbDocGettingStartedIntroductionEN', 'OrbDocGettingStartedIntroductionENDocEx1Counting', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocGettingStartedIntroductionEN', trait: 'OrbDocGettingStartedIntroductionENDocEx1Counting' } }); })({ event: "RESET" as const }));
        _fields['count'] = 0;
        fields = { ...(fields ?? {}), 'count': _fields['count'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'ready';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocGettingStartedIntroductionENDocEx1CountingSlot[]; clear: OrbDocGettingStartedIntroductionENDocEx1CountingSlot[] } => {
    switch (fromState) {
      case 'ready':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocGettingStartedIntroductionENDocEx1CountingSlot[], clear: [] as OrbDocGettingStartedIntroductionENDocEx1CountingSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocGettingStartedIntroductionENCounter': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocGettingStartedIntroductionENDocEx1CountingEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocGettingStartedIntroductionENDocEx1Counting': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocGettingStartedIntroductionENDocEx1CountingEventPayloadMap]: (payload?: OrbDocGettingStartedIntroductionENDocEx1CountingEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'INCREMENT': () => dispatch('INCREMENT'),
    'DECREMENT': () => dispatch('DECREMENT'),
    'RESET': () => dispatch('RESET'),
    'MOVED': (payload?: OrbDocGettingStartedIntroductionENDocEx1CountingEventPayloadMap['MOVED']) => dispatch('MOVED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocGettingStartedIntroductionEN.OrbDocGettingStartedIntroductionENDocEx1Counting', ['INIT', 'INCREMENT', 'DECREMENT', 'RESET'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocGettingStartedIntroductionENDocEx1Counting', (): TraitStateSnapshot => ({
      traitName: 'OrbDocGettingStartedIntroductionENDocEx1Counting',
      currentState: internalStateRef.current.machineState,
      states: ['ready'],
      events: ['INIT', 'INCREMENT', 'DECREMENT', 'RESET', 'MOVED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
